import { useCallback, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import { Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { ProductVariantNameCell, useSnackbar } from "@/features/ui";
import PropTypes from "prop-types";

export const GeneralProductSection = ({ editMode = false }) => {
  const { control } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "salesOrderProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "salesOrderProductVariants",
  });

  const handleDeleteClick = useCallback(
    (id) => () => {
      const variant = productVariants.find((v) => v.id === id);

      // In edit mode, validate delivered and invoiced quantities
      if (
        editMode &&
        (variant.deliveredQuantity > 0 || variant.invoicedQuantity > 0)
      ) {
        showSnackbar(
          "Tidak dapat menghapus produk yang sudah dikirim atau difaktur",
          "error",
        );
        return;
      }

      // Find index and remove
      const index = productVariants.findIndex((variant) => variant.id === id);
      if (index !== -1) {
        remove(index);
        showSnackbar("Produk berhasil dihapus", "success");
      }
    },
    [productVariants, remove, showSnackbar, editMode],
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Nama",
        flex: 7,
        renderCell: ({ row: { name, sku } }) => (
          <ProductVariantNameCell name={name} sku={sku} />
        ),
      },
      {
        field: "quantity",
        headerName: "Qty",
        type: "number",
        valueFormatter: (value) => formatNumber(value),
        editable: true,
        flex: 1.5,
      },
      { field: "unitOfMeasurement", headerName: "Unit", flex: 1 },
      ...(editMode
        ? [
            {
              field: "deliveredQuantity",
              headerName: "Dikirim",
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              flex: 1.5,
            },
            {
              field: "invoicedQuantity",
              headerName: "Difaktur",
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              flex: 1.5,
            },
          ]
        : []),
      {
        field: "costPerUnit",
        headerName: "@Harga",
        type: "number",
        flex: 2,
        editable: false, // Price not editable in SO
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "discount",
        headerName: "Diskon",
        type: "number",
        editable: true,
        flex: 2,
        valueSetter: (value, row) => {
          const newValue = value == null ? 0 : value;
          return { ...row, discount: newValue };
        },
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "totalPrice",
        headerName: "Total Harga",
        type: "number",
        flex: 2,
        valueGetter: (_, row) => {
          const { quantity, costPerUnit, discount } = row;
          return formatNumber(quantity * costPerUnit - discount || 0);
        },
      },
      { field: "note", headerName: "Catatan", flex: 3, editable: true },
      {
        field: "actions",
        type: "actions",
        flex: 1,
        getActions: ({ id, row }) => {
          // In edit mode, disable delete if delivered/invoiced
          const canDelete = editMode
            ? row.deliveredQuantity === 0 && row.invoicedQuantity === 0
            : true;

          // Build reason message for disabled state
          const reasons = [];
          if (editMode && row.deliveredQuantity > 0) {
            reasons.push("sudah dikirim");
          }
          if (editMode && row.invoicedQuantity > 0) {
            reasons.push("sudah difaktur");
          }
          const tooltipTitle = canDelete
            ? "Hapus"
            : `Tidak dapat dihapus (${reasons.join(", ")})`;

          return [
            <Tooltip key={id} title={tooltipTitle} arrow>
              <span>
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label="Hapus"
                  className="textPrimary"
                  onClick={handleDeleteClick(id)}
                  color="error"
                  disabled={!canDelete}
                />
              </span>
            </Tooltip>,
          ];
        },
      },
    ],
    [handleDeleteClick, editMode],
  );

  const processRowUpdate = useCallback(
    async (newRow) => {
      return new Promise((resolve, reject) => {
        // Clearing a numeric cell commits null (MUI's numeric valueParser),
        // which slips past the guards below and reaches the payload. Coerce
        // to 0 first.
        const row = {
          ...newRow,
          quantity: Number(newRow.quantity) || 0,
          costPerUnit: Number(newRow.costPerUnit) || 0,
          discount: Number(newRow.discount) || 0,
        };
        const {
          quantity,
          discount,
          maxQuantity,
          deliveredQuantity,
          invoicedQuantity,
          costPerUnit,
        } = row;
        const totalCost = quantity * costPerUnit;

        // Validation 1: Minimum quantity (cannot reduce below delivered/invoiced)
        const minQuantity = Math.max(
          deliveredQuantity || 0,
          invoicedQuantity || 0,
        );
        if (quantity < minQuantity) {
          const reasons = [];
          if (deliveredQuantity > 0) {
            reasons.push(`${deliveredQuantity} sudah dikirim`);
          }
          if (invoicedQuantity > 0) {
            reasons.push(`${invoicedQuantity} sudah difaktur`);
          }
          const reasonText = reasons.join(", ");
          reject(
            new Error(
              `Kuantitas tidak boleh kurang dari ${minQuantity} (${reasonText})`,
            ),
          );
          return;
        }

        // Validation 2: Maximum quantity (if has sales request link)
        if (
          maxQuantity !== null &&
          maxQuantity !== undefined &&
          quantity > maxQuantity
        ) {
          reject(
            new Error(
              `Kuantitas tidak boleh lebih dari ${maxQuantity} (melebihi PO)`,
            ),
          );
          return;
        }

        // Validation 3: Basic validations
        if (quantity < 1) {
          reject(new Error("Kuantitas tidak boleh kurang dari 1"));
          return;
        }

        if (discount < 0) {
          reject(new Error("Diskon tidak boleh kurang dari 0"));
          return;
        }

        if (discount > totalCost) {
          reject(new Error("Diskon tidak boleh lebih besar dari Total Harga"));
          return;
        }

        // Update form state
        const index = productVariants.findIndex((v) => v.id === row.id);
        if (index !== -1) {
          update(index, row);
          resolve(row);
        } else {
          reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
        }
      });
    },
    [productVariants, update],
  );

  const handleProcessRowUpdateError = useCallback(
    (error) => {
      showSnackbar(error.message, "error");
    },
    [showSnackbar],
  );

  return (
    <Stack spacing={2.5}>
      <Typography variant="h6">Pesanan Produk Varian</Typography>
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={productVariants || []}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        initialState={{
          sorting: { sortModel: [{ field: "name", sort: "asc" }] },
        }}
      />
    </Stack>
  );
};

GeneralProductSection.propTypes = { editMode: PropTypes.bool };
